import React, { useState, useRef } from 'react';
import { uploadImage, resolveImageUrl } from '../services/api';

const ImageDropzone = ({ value, onChange, label = 'Image Upload', fallback = '/images/products/acp_cladding.jpg' }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [showUrlInput, setShowUrlInput] = useState(false);
    const [urlText, setUrlText] = useState('');
    const fileInputRef = useRef(null);

    const getPreviewUrl = (img) => {
        if (!img) return '';
        if (typeof img === 'string') {
            if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:')) return img;
            return resolveImageUrl(img);
        }
        return '';
    };

    const handleFileSelect = async (file) => {
        if (!file || !file.type.startsWith('image/')) {
            alert('Please select a valid image file (JPG, PNG, WEBP, SVG).');
            return;
        }

        setUploading(true);
        try {
            const res = await uploadImage(file);
            setUploading(false);
            if (res && res.success && res.url) {
                onChange(res.url);
            } else {
                // Offline or fallback to Data URL preview
                const reader = new FileReader();
                reader.onload = (e) => {
                    onChange(e.target.result);
                };
                reader.readAsDataURL(file);
            }
        } catch (err) {
            setUploading(false);
            const reader = new FileReader();
            reader.onload = (e) => {
                onChange(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleInputChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFileSelect(e.target.files[0]);
        }
    };

    const previewUrl = getPreviewUrl(value);

    return (
        <div className="admin-image-dropzone-wrapper m-b20">
            <label className="admin-label">{label}</label>

            {/* PREVIEW CONTAINER IF IMAGE VALUE EXISTS */}
            {value && !uploading ? (
                <div className="image-preview-card" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    padding: '12px 16px',
                    backgroundColor: '#ffffff',
                    border: '2px solid #10b981',
                    borderRadius: '10px',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.08)'
                }}>
                    <div style={{ width: '70px', height: '55px', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#f1f5f9', flexShrink: 0 }}>
                        <img
                            src={previewUrl}
                            alt="Uploaded Preview"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                            }}
                        />
                    </div>
                    <div style={{ flexGrow: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {value.startsWith('data:') ? 'Local Image File Selected' : value}
                        </div>
                        <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '600', display: 'inline-block', marginTop: '2px' }}>
                            Image Loaded Successfully
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                        <button
                            type="button"
                            className="admin-btn admin-btn-secondary"
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                        >
                            <i className="fa fa-refresh m-r5" /> Change
                        </button>
                        <button
                            type="button"
                            className="admin-btn"
                            onClick={() => onChange('')}
                            style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none' }}
                        >
                            <i className="fa fa-trash" />
                        </button>
                    </div>
                </div>
            ) : (
                /* DRAG AND DROP ZONE */
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    style={{
                        border: isDragging ? '2px dashed #10b981' : '2px dashed #cbd5e1',
                        backgroundColor: isDragging ? 'rgba(16, 185, 129, 0.05)' : '#f8fafc',
                        borderRadius: '12px',
                        padding: '25px 20px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                    }}
                >
                    {uploading ? (
                        <div style={{ padding: '15px' }}>
                            <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: '28px', color: '#10b981' }} />
                            <p style={{ margin: '10px 0 0 0', fontSize: '14px', fontWeight: '600', color: '#334155' }}>
                                Uploading image file to server...
                            </p>
                        </div>
                    ) : (
                        <>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '50%',
                                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                color: '#10b981',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 12px auto',
                                fontSize: '20px'
                            }}>
                                <i className="fa fa-cloud-upload" />
                            </div>
                            <h5 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>
                                Drag &amp; Drop Image Here, or <span style={{ color: '#10b981', textDecoration: 'underline' }}>Browse Files</span>
                            </h5>
                            <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                                Supports JPG, PNG, WEBP, SVG (Up to 5MB)
                            </p>
                        </>
                    )}
                </div>
            )}

            {/* HIDDEN FILE INPUT */}
            <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleInputChange}
                style={{ display: 'none' }}
            />

            {/* DIRECT URL TOGGLE OPTION */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                <button
                    type="button"
                    onClick={() => { setShowUrlInput(!showUrlInput); setUrlText(value || ''); }}
                    style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
                >
                    {showUrlInput ? 'Hide URL Link Input' : 'Or Paste Direct Image Link'}
                </button>
            </div>

            {showUrlInput && (
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                    <input
                        type="text"
                        className="admin-input"
                        placeholder="e.g. /images/products/acp_cladding.jpg or https://..."
                        value={urlText}
                        onChange={(e) => setUrlText(e.target.value)}
                        style={{ fontSize: '13px' }}
                    />
                    <button
                        type="button"
                        className="admin-btn admin-btn-primary"
                        onClick={() => { onChange(urlText); setShowUrlInput(false); }}
                        style={{ padding: '6px 14px', fontSize: '13px', flexShrink: 0 }}
                    >
                        Apply Link
                    </button>
                </div>
            )}
        </div>
    );
};

export default ImageDropzone;
